import { isEmpty, isEqual } from "lodash";

import { firestore, MODEL_COLL } from "_firebase";

import { getCliResults, sendH2kSimulation, runH2kAzureSimulation } from "utils/results/api";
import { captureResultsNotFound } from "utils/sentry/captureResultsNotFound";

import { actions } from "features/Model/_ducks";
import { saveModel } from "features/Model/_ducks/thunk";

import types from "./types";

const { updateModalDetails, fetchModelData, getLinkedResultsStatus, changeSimulationStatus, setExportErrors } = actions;

const toggleResultsLoading = (isLoading) => ({
    type: types.TOGGLE_RESULTS_LOADING,
    isLoading,
});

const setError = (error) => ({
    type: types.SET_RESULTS_ERROR,
    error,
});

const resetResults = () => ({
    type: types.RESET_RESULTS,
});

const changePackage = (pkg) => ({
    type: types.CHANGE_PACKAGE,
    pkg,
});

const changeCase = (cse) => ({
    type: types.CHANGE_CASE,
    cse,
});

// DashboardAPI catches axios errors and returns them, so Promise.all still resolves.
const getFailedCliResult = (results = []) =>
    results.find((result) => result?.isAxiosError || result?.response?.status >= 400);

const RESULTS_NOT_FOUND_ERROR = "Results not found.";

const isResultsNotFoundError = (result) => {
    const status = result?.response?.status;
    const errorMessage = result?.response?.data?.error || result?.response?.data?.message || "";

    return (
        status === 404 ||
        errorMessage === RESULTS_NOT_FOUND_ERROR ||
        String(errorMessage).toLowerCase().includes("results not found")
    );
};

const getCliResultsErrorMessage = (result) => {
    if (isResultsNotFoundError(result)) {
        return RESULTS_NOT_FOUND_ERROR;
    }

    return result?.response?.data?.error || result?.message || true;
};

// use this function to implement linkedBaseline results to upgrades table
// cliId comes from modelDetails
const getResults =
    ({ cliIds = [], modelId }) =>
    async (dispatch, getState) => {
        if (!cliIds.length) {
            return;
        }

        dispatch(toggleResultsLoading(true));
        return Promise.all(cliIds.map((cliId) => getCliResults({ cliId, modelId })))
            .then((results) => {
                const failedResult = getFailedCliResult(results);
                if (failedResult) {
                    throw failedResult;
                }

                const resultData = results.reduce((obj, { data }) => {
                    const { metadata: { upgradePackageKey = "" } = {} } = data || {};

                    if (!upgradePackageKey) {
                        return {
                            ...obj,
                            base: data,
                        };
                    }

                    return {
                        ...obj,
                        [upgradePackageKey]: data,
                        base: data,
                    };
                }, {});

                dispatch({
                    type: types.SET_RESULTS,
                    packageResults: resultData,
                });
                dispatch(toggleResultsLoading(false));
                dispatch(setError(false));
            })
            .catch(async (result) => {
                const error = getCliResultsErrorMessage(result);

                if (isResultsNotFoundError(result)) {
                    const {
                        model: { modelData = {}, review: { exportErrors = [] } = {} } = {},
                        user = {},
                    } = getState();

                    await captureResultsNotFound({
                        modelId,
                        modelData,
                        user,
                        source: "getResults",
                        exportErrorsFromState: exportErrors,
                    });
                }

                dispatch(setError(error));
                dispatch(toggleResultsLoading(false));
            });
    };

const getLinkedResults =
    ({ cliIds = [], modelId }) =>
    async (dispatch, getState) => {
        if (!cliIds.length) {
            return;
        }
        dispatch(toggleResultsLoading(true));
        return (
            Promise.all(cliIds.map((cliId) => getCliResults({ cliId, modelId })))
                .then((results) => {
                    const failedResult = getFailedCliResult(results);
                    if (failedResult) {
                        throw failedResult;
                    }

                    const resultData = results.reduce((obj, { data }) => {
                        const { metadata: { upgradePackageKey = "" } = {} } = data || {};
                        dispatch(getLinkedResultsStatus(true));
                        if (!upgradePackageKey) {
                            return {
                                ...obj,
                                base: data,
                            };
                        }

                        return {
                            ...obj,
                            [upgradePackageKey]: data,
                            base: data,
                        };
                    }, {});

                    dispatch({
                        type: types.SET_LINKED_RESULTS,
                        packageResults: resultData,
                    });
                    dispatch(toggleResultsLoading(false));
                    dispatch(setError(false));
                })
                // no results, communicate this error with SelectModel
                // on fetch timeout (error), set isLoading to false in SelectModel
                .catch(async (result) => {
                    const error = getCliResultsErrorMessage(result);
                    dispatch(setError(error));
                    dispatch(toggleResultsLoading(false));
                    dispatch({
                        type: types.SET_LINKED_RESULTS,
                        packageResults: {},
                    });
                    dispatch(getLinkedResultsStatus(false));

                    if (isResultsNotFoundError(result)) {
                        const {
                            model: { modelData = {}, review: { exportErrors = [] } = {} } = {},
                            user = {},
                        } = getState();

                        await captureResultsNotFound({
                            modelId,
                            modelData,
                            user,
                            source: "getLinkedResults",
                            exportErrorsFromState: exportErrors,
                        });
                    }
                })
        );
    };

const clearLinkedResults = () => async (dispatch) => {
    dispatch({
        type: types.SET_LINKED_RESULTS,
        packageResults: {},
    });
};

const runSimulation =
    ({ packages: selectedPackages = [] }) =>
    async (dispatch, getState) => {
        const {
            model: { modelId = "", modelDetails: { lastCliRun: { cliId: cliObj = {} } = {} } = {} } = {},
            user: { uid: userId = "" } = {},
        } = getState();

        dispatch(updateModalDetails({ isSimulationRunning: true }));
        await changeSimulationStatus("RUNNING", modelId);

        await sendH2kSimulation({ modelId, userId, packages: selectedPackages }).catch(
            ({ response: { data: { error } = {} } = {} }) => {
                dispatch(updateModalDetails({ isSimulationRunning: false }));
                dispatch(setError(error));
                dispatch(toggleResultsLoading(false));
            }
        );

        let unSubResultsChanges = firestore.doc(`${MODEL_COLL}/${modelId}`).onSnapshot((doc) => {
            const { modelDetails: { lastCliRun = {} } = {}, isSimulationRunning } = doc.data();
            const { cliId = {} } = lastCliRun;

            const runIds = Object.values(cliId).map(({ runId = "" }) => runId);

            if (!isEmpty(cliId) && !isEqual(cliId, cliObj) && !isSimulationRunning) {
                dispatch(getResults({ cliIds: runIds, modelId })).then(() => {
                    dispatch(updateModalDetails({ simulationRunning: false, lastCliRun }));
                });
                unSubResultsChanges();
            }
        });
    };

const runAzureSimulation =
    ({ packages: selectedPackages = [], runLocation = "review" }) =>
    async (dispatch, getState) => {
        const {
            model: { modelId = "", modelDetails: { lastCliRun: { cliId: cliObj = {} } = {} } = {} } = {},
            user: { uid: userId = "" } = {},
        } = getState();

        await dispatch(updateModalDetails({ isSimulationRunning: true }));
        await dispatch(changeSimulationStatus("RUNNING", modelId));

        await dispatch(saveModel(modelId, () => {}, false));

        try {
            const resultContainer =
                (await runH2kAzureSimulation({ modelId, userId, packages: selectedPackages })) || {};

            if (isEmpty(resultContainer)) {
                await dispatch(updateModalDetails({ isSimulationRunning: false }));
                await dispatch(changeSimulationStatus("NOT_FOUND", modelId));
                dispatch(setError({ error: "Result Container Empty." }));
                dispatch(toggleResultsLoading(false));
                return;
            }

            const {
                data: { cliId = {}, datetime = "", metadata: { errorList = [], overallSimResult = "" } = {} } = {},
            } = resultContainer;

            const runIds = Object.values(cliId).map(({ runId = "" }) => runId);

            if (!isEmpty(cliId) && !isEqual(cliId, cliObj)) {
                dispatch(getResults({ cliIds: runIds, modelId }))
                    .then(() => {
                        if (overallSimResult === "FAIL" && errorList.length > 0 && runLocation === "upgradesTable") {
                            return dispatch(setExportErrors(errorList, "simulation", () => {}));
                        }
                        return {};
                    })
                    .then(() =>
                        dispatch(updateModalDetails({ isSimulationRunning: false, lastCliRun: { cliId, datetime } }))
                    )
                    .then(() => dispatch(fetchModelData(modelId)))
                    .finally(async () => {
                        await dispatch(changeSimulationStatus("IDLE", modelId));
                    });
            }
        } catch (error) {
            dispatch(updateModalDetails({ isSimulationRunning: false }));
            await dispatch(changeSimulationStatus("IDLE", modelId));
            dispatch(setError(error.message));
            console.log("Error", error.message);
            dispatch(toggleResultsLoading(false));

            return;
        }
    };

export default {
    getResults,
    getLinkedResults,
    clearLinkedResults,
    toggleResultsLoading,
    runSimulation,
    runAzureSimulation,
    resetResults,
    changePackage,
    changeCase,
    setError,
};
